import type { UserRole } from '@compass/client/types/membership';
import type { Program } from '@compass/client/types/program';
import type { Role } from '@compass/client/types/role';
import { Building2, Check, ChevronDown, Plus, Trash2, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { RoleBadge } from '@/components/RoleBadge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { SimpleTooltip } from '@/components/ui/tooltip';
import { useAllOrganizationRoles } from '@/hooks/useAllOrganizationRoles';
import { useOrgAndPrograms } from '@/hooks/useOrgAndPrograms';
import { cn } from '@/lib/utils';

interface NewRoleAssignment {
  id: string; // temporary ID for tracking
  roleId: string;
  programId?: string; // only for program-level roles
}

export interface PendingChanges {
  addOrgRoles: Set<string>;
  removeOrgRoles: Set<string>;
  addProgramRoles: Map<string, Set<string>>; // programId -> roleIds
  removeProgramRoles: Map<string, Set<string>>; // programId -> roleIds
}

interface AssignedRolesSectionProps {
  organizationId: string;
  userRoles: UserRole[];
  onChangeRoles: (pendingChanges: PendingChanges) => void;
  loading?: boolean;
  maxHeight?: string;
}

// ============================================================================
// Private Components
// ============================================================================

/**
 * Loading skeleton for the assigned roles section
 */
const LoadingSkeleton: React.FC = () => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <Skeleton className="h-6 w-32" />
    </div>
    <div className="space-y-2">
      <div className="flex items-center gap-2 rounded-lg border p-2">
        <Skeleton className="h-8 w-8 rounded-md" />
        <Skeleton className="h-5 flex-1" />
        <Skeleton className="h-7 w-7 rounded-md" />
      </div>
    </div>
    <Skeleton className="h-10 w-full rounded-md" />
  </div>
);

/**
 * Empty state when no roles are assigned
 */
const EmptyRolesState: React.FC = () => (
  <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-8 text-center">
    <Building2 className="h-8 w-8 text-muted-foreground/50" />
    <h3 className="mt-3 font-medium text-sm">No roles assigned</h3>
    <p className="mt-1 text-xs text-muted-foreground">
      Click &quot;Assign Role&quot; to add roles to this user
    </p>
  </div>
);

/**
 * Single role display card with delete button
 */
interface RoleCardProps {
  role: {
    id: string;
    name: string;
    level: string;
    programId?: string;
    isPending: boolean;
  };
  program?: Program;
  onRemove: () => void;
}

const RoleCard: React.FC<RoleCardProps> = ({ role, program, onRemove }) => {
  const isProgramLevel = role.level === 'program';

  // Create a UserRole object for RoleBadge
  const userRole = {
    id: role.id,
    name: role.name,
    level: role.level,
  };

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-lg border p-2',
        role.isPending && 'bg-muted/50',
      )}
    >
      {/* Role Badge - Icon Only */}
      <RoleBadge role={userRole} size="md" showName={false} />

      {/* Role Name and Program Info */}
      <div className="flex-1 min-w-0 flex items-center gap-1">
        <div className="text-sm font-medium">{role.name}</div>
        {isProgramLevel && program && (
          <div className="text-xs text-muted-foreground">• {program.name}</div>
        )}
      </div>

      {/* Delete Button */}
      <SimpleTooltip content="Click to remove role" side="left">
        <Button
          variant="ghost"
          size="icon"
          className="shrink-0 h-7 w-7 text-destructive hover:text-destructive/80"
          onClick={onRemove}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </SimpleTooltip>
    </div>
  );
};

/**
 * Role selector dropdown with descriptions
 */
interface RoleSelectorProps {
  value: string;
  onValueChange: (roleId: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availableOrgRoles: Role[];
  availableProgramRoles: Role[];
  selectedRoleName?: string;
}

const RoleSelector: React.FC<RoleSelectorProps> = ({
  value,
  onValueChange,
  open,
  onOpenChange,
  availableOrgRoles,
  availableProgramRoles,
  selectedRoleName,
}) => (
  <Select value={value} onValueChange={onValueChange} open={open} onOpenChange={onOpenChange}>
    <SelectTrigger className="flex-1">
      <SelectValue placeholder="Select role...">{selectedRoleName || 'Select role...'}</SelectValue>
    </SelectTrigger>
    <SelectContent>
      {availableOrgRoles.map(role => (
        <SelectItem key={role.id} value={role.id || ''}>
          <div className="flex flex-col">
            <span className="font-medium">{role.name}</span>
            {role.description && (
              <span className="text-xs text-muted-foreground">{role.description}</span>
            )}
          </div>
        </SelectItem>
      ))}
      {availableProgramRoles.map(role => (
        <SelectItem key={role.id} value={role.id || ''}>
          <div className="flex flex-col">
            <span className="font-medium">{role.name}</span>
            {role.description && (
              <span className="text-xs text-muted-foreground">{role.description}</span>
            )}
          </div>
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

/**
 * Multi-program selector combobox (allows selecting multiple programs)
 */
interface MultiProgramSelectorProps {
  values: string[];
  onToggle: (programId: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  programs: Program[];
}

const MultiProgramSelector: React.FC<MultiProgramSelectorProps> = ({
  values,
  onToggle,
  open,
  onOpenChange,
  programs,
}) => {
  const selectedCount = values.length;
  const selectedPrograms = programs.filter(p => values.includes(p.id || ''));

  // Display text for the trigger button
  const getDisplayText = () => {
    if (selectedCount === 0) return 'Select programs...';
    if (selectedCount === 1) return selectedPrograms[0]?.name || 'Select programs...';
    return `${selectedCount} programs selected`;
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-48 justify-between"
        >
          <span
            className={cn(
              'truncate',
              selectedCount > 0 ? 'text-foreground' : 'text-muted-foreground',
            )}
          >
            {getDisplayText()}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search programs..." className="h-9" />
          <CommandList className="max-h-48 overflow-y-auto" onWheel={e => e.stopPropagation()}>
            <CommandEmpty>No program found.</CommandEmpty>
            <CommandGroup>
              {programs.map(program => {
                const isSelected = values.includes(program.id || '');
                return (
                  <CommandItem
                    key={program.id}
                    value={program.name || ''}
                    onSelect={() => {
                      onToggle(program.id || '');
                    }}
                  >
                    <div
                      className={cn(
                        'mr-2 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border',
                        isSelected
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-muted-foreground/50',
                      )}
                    >
                      {isSelected && <Check className="h-3 w-3" />}
                    </div>
                    {program.name}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

/**
 * New role assignment row with role/program selection and confirm/cancel buttons
 */
interface RoleAssignmentRowProps {
  assignment: NewRoleAssignment;
  availableOrgRoles: Role[];
  availableProgramRoles: Role[];
  programs: Program[];
  roleSelectOpen: boolean;
  programComboboxOpen: boolean;
  selectedProgramIds: string[];
  onRoleSelectOpenChange: (open: boolean) => void;
  onProgramComboboxOpenChange: (open: boolean) => void;
  onRoleChange: (roleId: string) => void;
  onProgramToggle: (programId: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

const RoleAssignmentRow: React.FC<RoleAssignmentRowProps> = ({
  assignment,
  availableOrgRoles,
  availableProgramRoles,
  programs,
  roleSelectOpen,
  programComboboxOpen,
  selectedProgramIds,
  onRoleSelectOpenChange,
  onProgramComboboxOpenChange,
  onRoleChange,
  onProgramToggle,
  onConfirm,
  onCancel,
}) => {
  const isOrgRole = availableOrgRoles.some(r => r.id === assignment.roleId);
  const isProgramRole = availableProgramRoles.some(r => r.id === assignment.roleId);
  const canConfirm =
    assignment.roleId && (isOrgRole || (isProgramRole && selectedProgramIds.length > 0));

  // Get selected role name for display
  const selectedRole = [...availableOrgRoles, ...availableProgramRoles].find(
    r => r.id === assignment.roleId,
  );

  return (
    <div className="flex items-center gap-2 rounded-lg border border-dashed border-primary p-2">
      {/* Role Selection */}
      <RoleSelector
        value={assignment.roleId}
        onValueChange={onRoleChange}
        open={roleSelectOpen}
        onOpenChange={onRoleSelectOpenChange}
        availableOrgRoles={availableOrgRoles}
        availableProgramRoles={availableProgramRoles}
        selectedRoleName={selectedRole?.name}
      />

      {/* Program Selection (only for program-level roles) - supports multiple selection */}
      {isProgramRole && (
        <MultiProgramSelector
          values={selectedProgramIds}
          onToggle={onProgramToggle}
          open={programComboboxOpen}
          onOpenChange={onProgramComboboxOpenChange}
          programs={programs}
        />
      )}

      {/* Confirm Button (Plus icon) */}
      <SimpleTooltip content="Click to add role">
        <Button
          variant="default"
          size="icon"
          className={cn(
            'shrink-0 h-7 w-7',
            canConfirm ? 'animate-shake' : 'opacity-50 cursor-not-allowed',
          )}
          disabled={!canConfirm}
          onClick={onConfirm}
        >
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </SimpleTooltip>

      {/* Cancel Button (X icon) */}
      <SimpleTooltip content="Click to remove role" side="left">
        <Button
          variant="outline"
          size="icon"
          className="shrink-0 h-7 w-7 text-destructive border-destructive hover:text-destructive hover:bg-destructive/10 hover:border-destructive"
          onClick={onCancel}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </SimpleTooltip>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const AssignedRolesSection: React.FC<AssignedRolesSectionProps> = ({
  organizationId,
  userRoles,
  onChangeRoles,
  loading = false,
  maxHeight = 'max-h-[252px]',
}) => {
  // State for pending changes
  const [pendingChanges, setPendingChanges] = useState<PendingChanges>({
    addOrgRoles: new Set(),
    removeOrgRoles: new Set(),
    addProgramRoles: new Map(),
    removeProgramRoles: new Map(),
  });

  // State for new role assignments (rows before confirmation)
  const [newRoleAssignments, setNewRoleAssignments] = useState<NewRoleAssignment[]>([]);

  // State for program combobox open/closed
  const [programComboboxOpen, setProgramComboboxOpen] = useState<Record<string, boolean>>({});

  // State for role select open/closed
  const [roleSelectOpen, setRoleSelectOpen] = useState<Record<string, boolean>>({});

  // State for selected program IDs per assignment row (for multi-select)
  const [selectedProgramIds, setSelectedProgramIds] = useState<Record<string, string[]>>({});

  // Fetch available roles and programs
  const {
    organizationRoles: availableOrgRoles,
    programRoles: availableProgramRoles,
    isLoading: isLoadingRoles,
  } = useAllOrganizationRoles();

  const { programs } = useOrgAndPrograms();

  // Filter user roles by organization
  const currentOrgRoles = userRoles.filter(
    r => r.level === 'organization' && r.organization_id === organizationId,
  );

  const currentProgramRolesMap = new Map<string, UserRole[]>();
  userRoles
    .filter(r => r.level === 'program' && r.organization_id === organizationId)
    .forEach(role => {
      const progId = role.program_id || '';
      if (!currentProgramRolesMap.has(progId)) {
        currentProgramRolesMap.set(progId, []);
      }
      currentProgramRolesMap.get(progId)!.push(role);
    });

  // Notify parent of changes whenever pendingChanges updates
  useEffect(() => {
    onChangeRoles(pendingChanges);
  }, [pendingChanges, onChangeRoles]);

  // Organization role handlers
  const handleSelectOrgRole = (roleId: string) => {
    setPendingChanges(prev => {
      const newChanges = { ...prev };
      newChanges.addOrgRoles = new Set([roleId]);
      newChanges.removeOrgRoles = new Set();

      // Mark all other current roles for removal
      currentOrgRoles.forEach(role => {
        if (role.id !== roleId) {
          newChanges.removeOrgRoles.add(role.id || '');
        }
      });

      return newChanges;
    });
  };

  const handleRemoveAllOrgRoles = () => {
    setPendingChanges(prev => {
      const newChanges = { ...prev };
      newChanges.addOrgRoles = new Set();
      newChanges.removeOrgRoles = new Set(currentOrgRoles.map(r => r.id || ''));
      return newChanges;
    });
  };

  // Program role handlers
  const handleRemoveProgramMembership = (programId: string) => {
    setPendingChanges(prev => {
      const newChanges = { ...prev };
      newChanges.addProgramRoles = new Map(prev.addProgramRoles);
      newChanges.removeProgramRoles = new Map(prev.removeProgramRoles);

      // Remove all pending additions for this program
      newChanges.addProgramRoles.delete(programId);

      // Mark all current roles for this program for removal
      const currentRoles = currentProgramRolesMap.get(programId) || [];
      if (currentRoles.length > 0) {
        if (!newChanges.removeProgramRoles.has(programId)) {
          newChanges.removeProgramRoles.set(programId, new Set());
        }
        currentRoles.forEach(role => {
          newChanges.removeProgramRoles.get(programId)!.add(role.id || '');
        });
      }

      return newChanges;
    });
  };

  // Handlers for new role assignment rows
  const handleAddNewRoleRow = () => {
    const newId = Date.now().toString();
    setNewRoleAssignments(prev => [...prev, { id: newId, roleId: '', programId: undefined }]);
    // Automatically open the role select dropdown for the new row
    setRoleSelectOpen(prev => ({ ...prev, [newId]: true }));
  };

  const handleRemoveRoleRow = (id: string) => {
    setNewRoleAssignments(prev => prev.filter(row => row.id !== id));
    // Clean up the select open state for this row
    setRoleSelectOpen(prev => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
    // Clean up the selected program IDs for this row
    setSelectedProgramIds(prev => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  const handleRoleChange = (id: string, roleId: string) => {
    setNewRoleAssignments(prev =>
      prev.map(row => (row.id === id ? { ...row, roleId, programId: undefined } : row)),
    );
    // Clear selected programs when role changes
    setSelectedProgramIds(prev => ({ ...prev, [id]: [] }));
  };

  const handleProgramToggle = (id: string, programId: string) => {
    setSelectedProgramIds(prev => {
      const currentIds = prev[id] || [];
      const isSelected = currentIds.includes(programId);
      const newIds = isSelected
        ? currentIds.filter(pId => pId !== programId)
        : [...currentIds, programId];
      return { ...prev, [id]: newIds };
    });
  };

  const handleConfirmRoleAssignment = (assignment: NewRoleAssignment) => {
    const selectedRole = [...availableOrgRoles, ...availableProgramRoles].find(
      r => r.id === assignment.roleId,
    );

    if (!selectedRole) return;

    // Determine if it's an org or program role
    const isOrgRole = availableOrgRoles.some(r => r.id === assignment.roleId);
    const isProgramRole = availableProgramRoles.some(r => r.id === assignment.roleId);

    // Get the selected program IDs for this assignment row
    const programIdsForRow = selectedProgramIds[assignment.id] || [];

    if (isOrgRole) {
      // Only one org-level role allowed - replace existing
      handleSelectOrgRole(assignment.roleId);
    } else if (isProgramRole && programIdsForRow.length > 0) {
      // Handle multiple programs - create a role assignment for each selected program
      setPendingChanges(prev => {
        const newChanges = { ...prev };
        newChanges.addProgramRoles = new Map(prev.addProgramRoles);
        newChanges.removeProgramRoles = new Map(prev.removeProgramRoles);

        // Process each selected program
        programIdsForRow.forEach(programId => {
          // Mark all current roles for this program for removal
          const currentRoles = currentProgramRolesMap.get(programId) || [];
          if (currentRoles.length > 0) {
            if (!newChanges.removeProgramRoles.has(programId)) {
              newChanges.removeProgramRoles.set(programId, new Set());
            }
            currentRoles.forEach(role => {
              if (role.id) {
                newChanges.removeProgramRoles.get(programId)!.add(role.id);
              }
            });
          }

          // Set the new role (replacing any pending additions)
          newChanges.addProgramRoles.set(programId, new Set([assignment.roleId]));
        });

        return newChanges;
      });
    }

    // Remove the row after confirmation
    handleRemoveRoleRow(assignment.id);
  };

  // Build a list of all roles to display (current + pending additions - pending removals)
  const getDisplayRoles = () => {
    const displayRoles: Array<{
      id: string;
      name: string;
      level: string;
      programId?: string;
      isPending: boolean;
    }> = [];

    // Add current organization roles (if not marked for removal)
    currentOrgRoles.forEach(role => {
      if (!pendingChanges.removeOrgRoles.has(role.id || '')) {
        displayRoles.push({
          id: role.id || '',
          name: role.name || '',
          level: 'organization',
          isPending: false,
        });
      }
    });

    // Add pending organization roles
    pendingChanges.addOrgRoles.forEach(roleId => {
      const role = availableOrgRoles.find(r => r.id === roleId);
      if (role) {
        displayRoles.push({
          id: role.id || '',
          name: role.name || '',
          level: 'organization',
          isPending: true,
        });
      }
    });

    // Add current program roles (if not marked for removal)
    currentProgramRolesMap.forEach((roles, programId) => {
      roles.forEach(role => {
        if (!pendingChanges.removeProgramRoles.get(programId)?.has(role.id || '')) {
          displayRoles.push({
            id: role.id || '',
            name: role.name || '',
            level: 'program',
            programId,
            isPending: false,
          });
        }
      });
    });

    // Add pending program roles
    pendingChanges.addProgramRoles.forEach((roleIds, programId) => {
      roleIds.forEach(roleId => {
        const role = availableProgramRoles.find(r => r.id === roleId);
        if (role) {
          displayRoles.push({
            id: role.id || '',
            name: role.name || '',
            level: 'program',
            programId,
            isPending: true,
          });
        }
      });
    });

    return displayRoles;
  };

  const displayRoles = getDisplayRoles();

  // Show loading skeleton
  if (loading || isLoadingRoles) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">
          Assigned Roles
          {displayRoles.length > 0 && (
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              ({displayRoles.length})
            </span>
          )}
        </h3>
      </div>

      {/* Display all currently assigned roles (org + program) */}
      {displayRoles.length > 0 ? (
        <div className={cn('space-y-2 overflow-y-auto', maxHeight)}>
          {displayRoles.map((role, index) => {
            const isProgramLevel = role.level === 'program';
            const program = programs.find(p => p.id === role.programId);

            return (
              <RoleCard
                key={`${role.id}-${role.programId || 'org'}-${index}`}
                role={role}
                program={program}
                onRemove={() => {
                  if (role.level === 'organization') {
                    handleRemoveAllOrgRoles();
                  } else if (isProgramLevel && role.programId) {
                    handleRemoveProgramMembership(role.programId);
                  }
                }}
              />
            );
          })}
        </div>
      ) : (
        <EmptyRolesState />
      )}

      {/* New role assignment form or Assign Role button */}
      {newRoleAssignments.length > 0 ? (
        <div className="space-y-2">
          {newRoleAssignments.map(assignment => (
            <RoleAssignmentRow
              key={assignment.id}
              assignment={assignment}
              availableOrgRoles={availableOrgRoles}
              availableProgramRoles={availableProgramRoles}
              programs={programs}
              roleSelectOpen={roleSelectOpen[assignment.id] || false}
              programComboboxOpen={programComboboxOpen[assignment.id] || false}
              selectedProgramIds={selectedProgramIds[assignment.id] || []}
              onRoleSelectOpenChange={open =>
                setRoleSelectOpen(prev => ({ ...prev, [assignment.id]: open }))
              }
              onProgramComboboxOpenChange={open =>
                setProgramComboboxOpen(prev => ({ ...prev, [assignment.id]: open }))
              }
              onRoleChange={roleId => handleRoleChange(assignment.id, roleId)}
              onProgramToggle={programId => handleProgramToggle(assignment.id, programId)}
              onConfirm={() => handleConfirmRoleAssignment(assignment)}
              onCancel={() => handleRemoveRoleRow(assignment.id)}
            />
          ))}
        </div>
      ) : (
        <Button
          variant="outline"
          className="w-full border-dashed border-primary text-primary hover:text-primary hover:bg-primary/10"
          onClick={handleAddNewRoleRow}
        >
          <Plus className="mr-2 h-4 w-4" />
          Assign Role
        </Button>
      )}
    </div>
  );
};
